// Primitive/Pill (Figma 7:27) — 22px, px-12 py-4, pill radius, 1px border,
// .type-labelSm. The optional leading 6px "lamp" circle is drawn as a filled
// dot tinted with the tone's own lamp colour.

import "./ui.css";
import type { ReactNode } from "react";
import { disclose } from "../reading/termOrigin";
import type { TermOrigin } from "../data/types";

export type BadgeTone = "neutral" | "nominal" | "flagged" | "danger" | "accent";

export type BadgeProps = {
  tone?: BadgeTone;
  /** Show the leading status lamp, as "published" / "in review" do. */
  lamp?: boolean;
  children: ReactNode;
};

export function Badge({ tone = "neutral", lamp = false, children }: BadgeProps) {
  return (
    <span className="maisight-ui-badge type-labelSm" data-tone={tone}>
      {lamp ? <span className="maisight-ui-badge-lamp" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export type OriginPillProps = { origin: TermOrigin };

// Origin disclosure is a product invariant (PRD 11): every glossary term
// discloses where its definition came from. The pill text is LOCKED —
// `spec` / `AI` / `edit` — and it is TEXT, never a glyph, because glyphs did
// not survive font-fallback testing.
//
// The strings are not written here. They come from `reading/termOrigin.ts`,
// which is the single place they live for the reader, the widget and now the
// dashboard alike; duplicating them here is exactly how the three surfaces
// would drift apart.
export function OriginPill({ origin }: OriginPillProps) {
  const { pillLabel, longLabel } = disclose(origin);
  return (
    <span
      className="maisight-ui-badge maisight-ui-origin-pill type-labelSm"
      data-tone="neutral"
      data-origin={origin}
      title={longLabel}
    >
      {pillLabel}
    </span>
  );
}
